/**
 * Iter 70 — Admin Featured Listings panel.
 *
 * Two tabs in one panel:
 *   • Featured — current roster of `is_featured=true` cars. Admin can
 *     tweak `featured_priority`, `featured_fee_percent` (extra commission)
 *     and unfeature.
 *   • Requests — cars with a pending vendor request. Admin approves
 *     (feature at chosen fee%) or dismisses.
 *
 * Every action hits `PUT /api/admin/cars/{id}/featured` or its DELETE
 * counterpart — see `routes/featured_cars.py`.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Sparkles, X, Check, Star, RotateCw, Eye, MousePointerClick, Calendar } from "lucide-react";

export default function AdminFeaturedPanel() {
  const [tab, setTab] = useState("featured"); // "featured" | "requests"
  const [featured, setFeatured] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({}); // {carId: {impressions, clicks, ctr}}
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // {carId: {priority, fee}}
  // Iter 70b — auto-rotate settings
  const [rotate, setRotate] = useState({ rotate_days: 0, auto_promote_requests: false });
  const [rotateBusy, setRotateBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r, s, rs] = await Promise.all([
        api.get("/admin/featured/list"),
        api.get("/admin/featured/requests"),
        api.get("/admin/featured/stats?days=30"),
        api.get("/admin/featured/rotate-settings"),
      ]);
      setFeatured(f.data || []);
      setRequests(r.data || []);
      setStats((s.data && s.data.cars) || {});
      setRotate({
        rotate_days: Number(rs.data?.rotate_days) || 0,
        auto_promote_requests: !!rs.data?.auto_promote_requests,
      });
    } catch (e) {
      toast.error("Could not load featured listings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (carId, key, value) =>
    setDrafts((d) => ({ ...d, [carId]: { ...(d[carId] || {}), [key]: value } }));

  const saveRow = async (car) => {
    const draft = drafts[car.id] || {};
    const body = {
      is_featured: true,
      featured_priority: Number.isFinite(Number(draft.priority))
        ? Number(draft.priority)
        : car.featured_priority || 0,
      featured_fee_percent: Number.isFinite(Number(draft.fee))
        ? Number(draft.fee)
        : car.featured_fee_percent || 0,
    };
    try {
      await api.put(`/admin/cars/${car.id}/featured`, body);
      toast.success(`Updated ${car.make} ${car.model}`);
      setDrafts((d) => {
        const next = { ...d };
        delete next[car.id];
        return next;
      });
      load();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    }
  };

  const unfeature = async (car) => {
    if (!confirm(`Remove featured slot for ${car.make} ${car.model}?`)) return;
    try {
      await api.delete(`/admin/cars/${car.id}/featured`);
      toast.success("Unfeatured");
      load();
    } catch (e) {
      toast.error("Could not unfeature", { description: e?.response?.data?.detail || e.message });
    }
  };

  const approveRequest = async (car) => {
    const req = car.featured_request || {};
    const feeInput = drafts[car.id]?.fee ?? req.max_fee_percent ?? 0;
    const priorityInput = drafts[car.id]?.priority ?? 100;
    try {
      await api.put(`/admin/cars/${car.id}/featured`, {
        is_featured: true,
        featured_priority: Number(priorityInput),
        featured_fee_percent: Number(feeInput),
        clear_request: true,
      });
      toast.success(`Approved — ${car.make} ${car.model} is now featured at ${feeInput}% fee`);
      load();
    } catch (e) {
      toast.error("Approval failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const rejectRequest = async (car) => {
    try {
      await api.put(`/admin/cars/${car.id}/featured`, { clear_request: true });
      toast.success("Request dismissed");
      load();
    } catch (e) {
      toast.error("Could not dismiss", { description: e?.response?.data?.detail || e.message });
    }
  };

  const saveRotate = async () => {
    setRotateBusy(true);
    try {
      await api.put("/admin/featured/rotate-settings", {
        rotate_days: Number(rotate.rotate_days) || 0,
        auto_promote_requests: !!rotate.auto_promote_requests,
      });
      toast.success(
        Number(rotate.rotate_days) === 0
          ? "Auto-rotate disabled"
          : `Auto-rotate set to ${rotate.rotate_days} days`,
      );
    } catch (e) {
      toast.error("Could not save rotate settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRotateBusy(false);
    }
  };

  const rotateNow = async () => {
    setRotateBusy(true);
    try {
      const r = await api.post("/admin/featured/rotate-now");
      const data = r.data || {};
      if (data.reason === "auto_rotate_disabled") {
        toast.info("Auto-rotate is currently disabled. Set a positive number of days first.");
      } else {
        toast.success(
          `Rotate cycle complete — ${data.rotated || 0} unfeatured${
            data.promoted ? `, ${data.promoted} auto-promoted from queue` : ""
          }`,
        );
        load();
      }
    } catch (e) {
      toast.error("Could not rotate", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRotateBusy(false);
    }
  };

  // Iter 70d — non-destructive rotation preview.
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const runPreview = async () => {
    setPreviewBusy(true);
    try {
      const r = await api.get("/admin/featured/rotate-preview");
      setPreview(r.data || {});
    } catch (e) {
      toast.error("Could not preview", { description: e?.response?.data?.detail || e.message });
    } finally {
      setPreviewBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid="admin-featured-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <h3 className="text-lg font-semibold">Featured listings</h3>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1">
          <button
            onClick={() => setTab("featured")}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              tab === "featured" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-600 hover:text-neutral-900"
            }`}
            data-testid="admin-featured-tab-featured"
          >
            Featured ({featured.length})
          </button>
          <button
            onClick={() => setTab("requests")}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              tab === "requests" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-600 hover:text-neutral-900"
            }`}
            data-testid="admin-featured-tab-requests"
          >
            Vendor requests
            {requests.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {requests.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <p className="text-xs text-neutral-500 mb-4">
        Featured cars appear on the homepage teaser (top) and the mid-page showcase grid.
        Priority sorts descending (higher = sooner). Fee % is extra commission the vendor
        agreed to pay for the slot — set to 0 for editorial picks.
      </p>

      {/* Iter 70b — auto-rotate settings block */}
      <div
        className="mb-6 rounded-xl border border-neutral-200 bg-neutral-50/70 p-4"
        data-testid="admin-featured-rotate-settings"
      >
        <div className="flex items-center gap-2 mb-3">
          <RotateCw className="h-4 w-4 text-neutral-600" />
          <h4 className="text-sm font-semibold text-neutral-900">Auto-rotate</h4>
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">
              Rotate cars after (days)
            </label>
            <Input
              type="number"
              min={0}
              max={90}
              value={rotate.rotate_days}
              onChange={(e) => setRotate((r) => ({ ...r, rotate_days: e.target.value }))}
              className="h-9 w-28"
              data-testid="admin-featured-rotate-days-input"
            />
            <div className="text-[10px] text-neutral-500 mt-1">0 = disabled</div>
          </div>
          <label className="flex items-center gap-2 pb-1.5 cursor-pointer">
            <Switch
              checked={rotate.auto_promote_requests}
              onCheckedChange={(v) =>
                setRotate((r) => ({ ...r, auto_promote_requests: !!v }))
              }
              data-testid="admin-featured-auto-promote-switch"
            />
            <span className="text-xs text-neutral-700">
              Auto-promote pending vendor requests into freed slots
            </span>
          </label>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={runPreview}
              disabled={previewBusy}
              data-testid="admin-featured-rotate-preview"
              title="Show what the next rotation would change — without applying it"
            >
              <Calendar className="h-3 w-3 mr-1" /> Preview
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rotateNow}
              disabled={rotateBusy}
              data-testid="admin-featured-rotate-now"
              title="Run the rotate cycle immediately without waiting 24h"
            >
              <RotateCw className={`h-3 w-3 mr-1 ${rotateBusy ? "animate-spin" : ""}`} /> Rotate now
            </Button>
            <Button
              size="sm"
              onClick={saveRotate}
              disabled={rotateBusy}
              data-testid="admin-featured-rotate-save"
              className="bg-neutral-900 hover:bg-neutral-800 text-white"
            >
              Save
            </Button>
          </div>
        </div>

        {preview && (
          <div
            className="mt-4 rounded-xl border border-blue-200 bg-blue-50/50 p-4"
            data-testid="admin-featured-rotate-preview-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-blue-900">Next rotation preview</div>
              <button
                onClick={() => setPreview(null)}
                className="text-blue-700 hover:text-blue-900"
                aria-label="Close preview"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {preview.reason === "auto_rotate_disabled" ? (
              <div className="text-xs text-blue-800/80">
                Auto-rotate is disabled — set a positive number of days above to preview.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="font-semibold text-blue-900 mb-1">
                    Would unfeature ({preview.would_rotate?.length || 0})
                  </div>
                  {preview.would_rotate?.length ? (
                    <ul className="space-y-1">
                      {preview.would_rotate.map((c) => (
                        <li key={c.id} className="text-blue-900/80">
                          {c.make} {c.model} <span className="text-blue-700/60">· since {c.featured_since?.slice(0, 10)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-blue-800/60 italic">Nothing aged past the threshold.</div>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-blue-900 mb-1">
                    Would auto-promote ({preview.would_promote?.length || 0})
                  </div>
                  {preview.would_promote?.length ? (
                    <ul className="space-y-1">
                      {preview.would_promote.map((c) => (
                        <li key={c.id} className="text-blue-900/80">
                          {c.make} {c.model}{" "}
                          <span className="rounded-full bg-emerald-100 text-emerald-900 px-1.5 py-0.5 text-[10px] font-bold ml-1">
                            {c.fee.toFixed(1)}% bid
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : preview.auto_promote_requests ? (
                    <div className="text-blue-800/60 italic">No pending vendor requests to promote.</div>
                  ) : (
                    <div className="text-blue-800/60 italic">Auto-promote is off; freed slots stay empty.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-neutral-500">Loading…</div>
      ) : tab === "featured" ? (
        featured.length === 0 ? (
          <div className="py-12 text-center text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-xl">
            No featured cars yet. Approve a vendor request or feature any car from its edit page.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-4">Car</th>
                  <th className="py-2 pr-4">Vendor</th>
                  <th className="py-2 pr-4">Priority</th>
                  <th className="py-2 pr-4">Fee %</th>
                  <th className="py-2 pr-4">30-day performance</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {featured.map((car) => {
                  const s = stats[car.id] || { impressions: 0, clicks: 0, ctr: 0 };
                  return (
                  <tr key={car.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-featured-row-${car.id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-neutral-900">
                        {car.make} {car.model} <span className="text-neutral-400">· {car.year}</span>
                      </div>
                      <div className="text-xs text-neutral-500">{car.location}</div>
                    </td>
                    <td className="py-3 pr-4 text-neutral-700">
                      {car.vendor?.business_name || car.vendor?.name || car.vendor?.email || "—"}
                    </td>
                    <td className="py-3 pr-4">
                      <Input
                        type="number"
                        min={0}
                        max={1000}
                        defaultValue={car.featured_priority ?? 0}
                        onChange={(e) => setDraft(car.id, "priority", e.target.value)}
                        className="h-8 w-20 text-sm"
                        data-testid={`admin-featured-priority-${car.id}`}
                      />
                    </td>
                    <td className="py-3 pr-4">
                      <Input
                        type="number"
                        min={0}
                        max={50}
                        step={0.5}
                        defaultValue={car.featured_fee_percent ?? 0}
                        onChange={(e) => setDraft(car.id, "fee", e.target.value)}
                        className="h-8 w-20 text-sm"
                        data-testid={`admin-featured-fee-${car.id}`}
                      />
                    </td>
                    <td className="py-3 pr-4" data-testid={`admin-featured-stats-${car.id}`}>
                      <div className="flex items-center gap-3 text-xs text-neutral-700">
                        <span className="inline-flex items-center gap-1" title="Impressions in the last 30 days">
                          <Eye className="h-3 w-3 text-neutral-500" />
                          {s.impressions.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center gap-1" title="Clicks in the last 30 days">
                          <MousePointerClick className="h-3 w-3 text-neutral-500" />
                          {s.clicks.toLocaleString()}
                        </span>
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            s.ctr >= 5
                              ? "bg-emerald-100 text-emerald-800"
                              : s.ctr >= 1
                              ? "bg-amber-100 text-amber-800"
                              : "bg-neutral-200 text-neutral-600"
                          }`}
                          title="Click-through rate over the last 30 days"
                        >
                          {s.ctr.toFixed(2)}% CTR
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          car.is_active
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-neutral-100 text-neutral-500 border border-neutral-200"
                        }`}
                      >
                        {car.is_active ? "Live" : "Paused"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => saveRow(car)}
                        className="mr-2"
                        data-testid={`admin-featured-save-${car.id}`}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unfeature(car)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`admin-featured-unfeature-${car.id}`}
                      >
                        <X className="h-3 w-3 mr-1" /> Unfeature
                      </Button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )
      ) : requests.length === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-xl">
          No pending vendor requests. Vendors can request from their car edit page.
        </div>
      ) : (
        <ul className="space-y-3">
          {requests.map((car) => {
            const req = car.featured_request || {};
            return (
              <li
                key={car.id}
                className="rounded-xl border border-amber-200 bg-amber-50/50 p-4"
                data-testid={`admin-featured-request-${car.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      <div className="font-semibold text-neutral-900">
                        {car.make} {car.model} · {car.year}
                      </div>
                    </div>
                    <div className="text-xs text-neutral-600">
                      Vendor: <strong>{car.vendor?.business_name || car.vendor?.name || "—"}</strong>{" "}
                      · {car.location}
                    </div>
                    <div className="text-xs text-neutral-600 mt-1">
                      Requested {new Date(req.requested_at).toLocaleString()} · willing to pay up to{" "}
                      <strong>{req.max_fee_percent}%</strong> extra commission
                    </div>
                    {req.note && (
                      <div className="mt-2 text-xs italic text-neutral-700 border-l-2 border-amber-300 pl-3">
                        “{req.note}”
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 min-w-[220px]">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-neutral-600">Priority</span>
                      <Input
                        type="number"
                        min={0}
                        max={1000}
                        defaultValue={100}
                        onChange={(e) => setDraft(car.id, "priority", e.target.value)}
                        className="h-8 w-20 text-sm"
                      />
                      <span className="text-neutral-600">Fee %</span>
                      <Input
                        type="number"
                        min={0}
                        max={50}
                        step={0.5}
                        defaultValue={req.max_fee_percent || 0}
                        onChange={(e) => setDraft(car.id, "fee", e.target.value)}
                        className="h-8 w-20 text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => approveRequest(car)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`admin-featured-approve-${car.id}`}
                      >
                        <Check className="h-3 w-3 mr-1" /> Approve & feature
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectRequest(car)}
                        data-testid={`admin-featured-reject-${car.id}`}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
